import { Box, Flex, HStack, Text, VStack } from "@chakra-ui/react";
import { FiFile, FiImage } from "react-icons/fi";
import {
  LunaMessage as LunaMessageType,
  LunaAttachment,
  formatBytes,
  isImageContentType,
} from "services/luna";
import { BlockRenderer, UIBlock } from "./blocks";
import { LunaActionCard } from "./LunaActionCard";
import { LunaMessageActions } from "./LunaMessageActions";
import { LunaThinkingSteps } from "./LunaThinkingSteps";

interface LunaMessageProps {
  message: LunaMessageType;
  sessionId: string;
  readOnly?: boolean;
  "data-message-id"?: string;
}

// Helper to extract blocks from content (handles nested array structure)
const getBlocksFromContent = (
  content: string | unknown[][] | unknown,
): UIBlock[] => {
  // If content is a string, wrap it as a text block
  if (typeof content === "string") {
    return content ? [{ type: "text", content }] : [];
  }

  // If content is an array (nested blocks from backend)
  if (Array.isArray(content)) {
    // Flatten nested arrays: [[{type, content}]] -> [{type, content}]
    const flattened = content.flat(2);
    return flattened.filter(
      (item): item is UIBlock =>
        typeof item === "object" && item !== null && "type" in item,
    );
  }

  return [];
};

// Read-only file chip (filename + size) — the same shape the admin dashboard
// renders for message attachments.
const MessageAttachmentChip = ({
  attachment,
}: {
  attachment: LunaAttachment;
}) => {
  const Icon = isImageContentType(attachment.content_type) ? FiImage : FiFile;
  return (
    <HStack
      spacing="2xs"
      align="center"
      bg="bg.container"
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="component.md"
      px="xs"
      py="2xs"
      maxW="240px"
      minW={0}
    >
      <Box as={Icon} size={14} color="text.body.tertiary" flexShrink={0} />
      <Text
        fontSize="sm"
        lineHeight={1}
        color="text.body.primary"
        isTruncated
        flex={1}
        minW={0}
      >
        {attachment.filename}
      </Text>
      <Text
        fontSize="xs"
        lineHeight={1}
        color="text.body.tertiary"
        flexShrink={0}
        whiteSpace="nowrap"
      >
        {formatBytes(attachment.size)}
      </Text>
    </HStack>
  );
};

const AttachmentChips = ({
  attachments,
  align,
}: {
  attachments: LunaAttachment[];
  align: "flex-start" | "flex-end";
}) => (
  <Flex justify={align} wrap="wrap" gap="2xs" w="100%">
    {attachments.map((attachment) => (
      <MessageAttachmentChip key={attachment.s3_key} attachment={attachment} />
    ))}
  </Flex>
);

export const LunaMessage = ({
  message,
  sessionId,
  readOnly,
  "data-message-id": dataMessageId,
}: LunaMessageProps) => {
  const isUser = message.role === "user";
  const isAssistant = message.role === "assistant" || message.role === "external";
  const attachments = message.attachments ?? [];

  // Skip non-user/assistant messages (tool, system) - they're handled elsewhere
  if (!isUser && !isAssistant) {
    return null;
  }

  // A pending input's card renders on its own, without the rating actions
  if (isAssistant && message.actions) {
    return (
      <Box w="100%" mb="xl" data-message-id={dataMessageId}>
        <LunaActionCard
          message={message}
          sessionId={sessionId}
          readOnly={readOnly}
        />
      </Box>
    );
  }

  // Get display content - for user messages, extract string; for assistant, get blocks
  const userContent =
    typeof message.content === "string" ? message.content : "";
  const contentBlocks = isAssistant
    ? getBlocksFromContent(message.content)
    : [];

  // Count completed tool executions for thinking steps
  const completedToolSteps =
    message.tool_executions?.filter(
      (t) => t.status === "completed" || t.status === "confirmed",
    ).length || 0;

  // Check if there are any tool executions (running or completed)
  const hasToolExecutions = (message.tool_executions?.length || 0) > 0;

  // Skip assistant messages that have no content to display
  if (
    isAssistant &&
    !hasToolExecutions &&
    contentBlocks.length === 0 &&
    attachments.length === 0 &&
    (!message.blocks || message.blocks.length === 0)
  ) {
    return null;
  }

  return (
    <Box w="100%" mb="xl" data-message-id={dataMessageId}>
      {isUser ? (
        // User message - right aligned bubble + any attachment chips
        <VStack align="stretch" spacing="2xs" w="100%">
          {userContent ? (
            <HStack justify="flex-end" w="100%">
              <Box
                bg="p.100"
                color="text.body.primary"
                px="md"
                py="sm"
                borderRadius="component.lg"
                maxW="80%"
                fontSize="md"
              >
                {userContent}
              </Box>
            </HStack>
          ) : null}
          {attachments.length > 0 && (
            <AttachmentChips attachments={attachments} align="flex-end" />
          )}
        </VStack>
      ) : (
        // Assistant message - full width with blocks
        <VStack align="stretch" spacing="md" w="100%">
          {/* Attachments (if any) render above the content, left-aligned */}
          {attachments.length > 0 && (
            <AttachmentChips attachments={attachments} align="flex-start" />
          )}

          {/* Message content - render blocks from content or message.blocks */}
          {message.blocks && message.blocks.length > 0 ? (
            <VStack align="stretch" spacing="md">
              {message.blocks.map((block, i) => (
                <BlockRenderer key={block.id || i} block={block} />
              ))}
            </VStack>
          ) : contentBlocks.length > 0 ? (
            <VStack align="stretch" spacing="md">
              {contentBlocks.map((block, i) => (
                <BlockRenderer key={block.id || i} block={block} />
              ))}
            </VStack>
          ) : null}

          {/* Thinking steps indicator - show when any tools exist (running or completed) */}
          {hasToolExecutions && (
            <LunaThinkingSteps
              sessionId={sessionId}
              stepCount={completedToolSteps}
              toolExecutions={message.tool_executions}
            />
          )}

          {/* Tool calls with confirmation UI (hidden in readOnly mode) */}
          {!readOnly &&
            message.tool_calls?.map((toolCall) =>
              toolCall.execution?.requires_confirmation ? (
                <BlockRenderer
                  key={toolCall.id}
                  block={{
                    type: "confirmation",
                    content: toolCall.execution?.confirmation_message,
                    id: toolCall.id,
                    sessionId,
                    executionId: toolCall.execution?.id,
                    status: toolCall.execution?.status,
                  }}
                />
              ) : null,
            )}

          {/* Message actions (rating, retry) - hidden in readOnly mode */}
          {!readOnly && isAssistant && !message.is_thinking && (
            <LunaMessageActions
              sessionId={sessionId}
              messageId={message.id}
              rating={message.rating}
            />
          )}
        </VStack>
      )}
    </Box>
  );
};
